// ════════════════════════════════════════════════════════════════════════════
// 📡 OfflineQueue — Phase 1 Resilience
// ════════════════════════════════════════════════════════════════════════════
// Drop-in replacement for naked fetch() to /api/pos-checkout.
// Behavior:
//   - Online → POSTs immediately (returns server response).
//   - Offline OR fetch fails (network/timeout) → queues to IndexedDB.
//   - Auto-flushes queue on:
//       * 'online' window event
//       * pollInterval (default 15s) when items are pending
//       * manual flush() call
//   - Idempotent — server's pos-checkout already de-dupes via txRef.
// ════════════════════════════════════════════════════════════════════════════

import { idb, OfflineTx } from './idb';

type Status = 'online' | 'offline' | 'syncing';

type Listener = (status: Status, pending: number, failed: number) => void;

// After this many 4xx attempts, the transaction is a permanent poison pill —
// purge it from IDB, log for operator visibility, and let the queue clear.
const MAX_4XX_ATTEMPTS = 5;

class OfflineQueueImpl {
  private listeners = new Set<Listener>();
  private status: Status = navigator.onLine ? 'online' : 'offline';
  private pending = 0;
  private failedCount = 0;
  private pollTimer: number | null = null;
  private coarseTimer: number | null = null;
  private flushing = false;

  constructor() {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    // Initial state
    void this.refreshPendingCount();
    if (navigator.onLine) void this.flush();
    // 3-minute coarse safety net — catches cases where the 15s timer was lost
    this.coarseTimer = window.setInterval(() => {
      if (navigator.onLine && this.pending > 0 && !this.flushing) {
        void this.flush();
      }
    }, 3 * 60 * 1000);
  }

  // ── Public API ──────────────────────────────────────────────────────────

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.status, this.pending, this.failedCount);
    return () => this.listeners.delete(fn);
  }

  /**
   * Submit a checkout. Returns:
   *   { ok: true, online: true,  data }  → server confirmed
   *   { ok: true, online: false }        → queued offline (will sync later)
   *   { ok: false, error }               → server rejected (4xx/5xx with body)
   */
  async submitCheckout(payload: any): Promise<
    | { ok: true; online: true; data: any }
    | { ok: true; online: false }
    | { ok: false; error: string }
  > {
    const txRef = payload?.txRef;
    if (!txRef) return { ok: false, error: 'Missing txRef' };

    // Try online first
    if (navigator.onLine) {
      try {
        const token = localStorage.getItem('kangavena:cashierToken') || '';
        const res = await fetch('/api/pos-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) return { ok: true, online: true, data };
        // Server rejected with a real error (e.g. insufficient stock).
        // Do NOT queue — this is a business validation failure, not a network issue.
        if (res.status >= 400 && res.status < 500) {
          return { ok: false, error: data?.error || `HTTP ${res.status}` };
        }
        // 5xx → queue and retry later
        await this.enqueue(payload);
        return { ok: true, online: false };
      } catch (err: any) {
        // Network/fetch failure → queue
        await this.enqueue(payload);
        return { ok: true, online: false };
      }
    }

    // Offline → queue immediately
    await this.enqueue(payload);
    return { ok: true, online: false };
  }

  async flush(): Promise<{ synced: number; failed: number; purged: number }> {
    if (this.flushing) return { synced: 0, failed: 0, purged: 0 };
    if (!navigator.onLine) return { synced: 0, failed: 0, purged: 0 };

    this.flushing = true; // set INSIDE try so finally always runs
    this.setStatus('syncing');

    let synced = 0, failed = 0, purged = 0;
    try {
      const all = await idb.getAll();
      for (const tx of all) {
        try {
          const token = (tx.payload as any)?.__token || localStorage.getItem('kangavena:cashierToken') || '';
          const res = await fetch('/api/pos-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(tx.payload),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok || data?.idempotent) {
            await idb.delete(tx.txRef);
            synced++;
          } else if (res.status >= 400 && res.status < 500) {
            // 4xx = permanent business-logic rejection (e.g. stock gone, invalid SKU).
            const newAttempts = (tx.attempts || 0) + 1;
            if (newAttempts >= MAX_4XX_ATTEMPTS) {
              // Poison pill: purge from IDB after MAX_4XX_ATTEMPTS so the badge clears.
              await idb.delete(tx.txRef);
              purged++;
              console.error(
                `[OfflineQueue] Purged poison-pill txRef=${tx.txRef} after ${newAttempts} failed attempts.`,
                `Last error: ${data?.error || `HTTP ${res.status}`}`,
                'Payload:', tx.payload,
              );
            } else {
              await idb.put({ ...tx, attempts: newAttempts, lastError: data?.error || `HTTP ${res.status}` });
              failed++;
            }
          } else {
            // 5xx / unknown — transient, keep for retry
            await idb.put({ ...tx, attempts: (tx.attempts || 0) + 1, lastError: `HTTP ${res.status}` });
            failed++;
          }
        } catch (err: any) {
          await idb.put({ ...tx, attempts: (tx.attempts || 0) + 1, lastError: err?.message || 'Network error' });
          failed++;
          // Lost connectivity mid-flush — stop trying to avoid false error counts
          if (!navigator.onLine) break;
        }
      }
    } finally {
      this.flushing = false;
      await this.refreshPendingCount();
      this.setStatus(navigator.onLine ? 'online' : 'offline');
      if (this.pending > 0 && navigator.onLine) this.startPolling();
      else this.stopPolling();
    }
    return { synced, failed, purged };
  }

  /** Force-clear a single stuck transaction by txRef (manager override). */
  async dismissFailed(txRef: string): Promise<void> {
    await idb.delete(txRef);
    await this.refreshPendingCount();
  }

  /** Force-clear ALL transactions that have a lastError set. */
  async dismissAllFailed(): Promise<number> {
    const all = await idb.getAll();
    const errored = all.filter(tx => tx.lastError);
    await Promise.all(errored.map(tx => idb.delete(tx.txRef)));
    await this.refreshPendingCount();
    return errored.length;
  }

  async listPending(): Promise<OfflineTx[]> {
    return idb.getAll();
  }

  getStatus(): Status { return this.status; }
  getPendingCount(): number { return this.pending; }
  getFailedCount(): number { return this.failedCount; }

  // ── Internals ────────────────────────────────────────────────────────────

  private async enqueue(payload: any) {
    await idb.put({
      txRef: payload.txRef,
      payload,
      createdAt: Date.now(),
      attempts: 0,
    });
    await this.refreshPendingCount();
    this.startPolling();
  }

  private async refreshPendingCount() {
    try {
      const all = await idb.getAll();
      this.pending = all.length;
      this.failedCount = all.filter(tx => !!tx.lastError).length;
      this.notify();
    } catch { /* IDB may be unavailable in private mode — silent */ }
  }

  private handleOnline() {
    this.setStatus('online');
    void this.flush();
  }

  private handleOffline() {
    this.setStatus('offline');
    this.stopPolling();
  }

  private setStatus(s: Status) {
    this.status = s;
    this.notify();
  }

  private notify() {
    for (const fn of this.listeners) fn(this.status, this.pending, this.failedCount);
  }

  private startPolling() {
    if (this.pollTimer != null) return;
    this.pollTimer = window.setInterval(() => {
      if (navigator.onLine && this.pending > 0 && !this.flushing) {
        void this.flush();
      }
    }, 15000);
  }

  private stopPolling() {
    if (this.pollTimer != null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

export const OfflineQueue = new OfflineQueueImpl();
export type { Status as OfflineStatus };
